import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { map, startWith, switchMap, tap } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import {
  CheckboxComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  NestedTemplateTriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { Field, ViewField, ViewForm } from '@core/views-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import { ViewFieldsService } from '../../view-fields.service';

@Component({
  selector: 'view-settings-fields',
  imports: [
    CdkDrag,
    CdkDropList,
    CheckboxComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NestedTemplateTriggerDirective,
  ],
  templateUrl: './view-settings-fields.component.html',
  styleUrl: './view-settings-fields.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSettingsFieldsComponent implements OnInit {
  public fieldsControl = input.required<FormControl<ViewField[]>>();
  public layoutControl = input.required<ViewForm['controls']['layout']>();

  public visibleFields: Field[];
  public fieldsSearch = new FormControl('');
  public selectedFieldsCount = 0;
  public availableFieldsCount = 0;

  public mainDropdown = inject(DropdownComponent);
  public viewsAnalytics = inject(ViewsAnalytics);

  private viewFieldsService = inject(ViewFieldsService);
  private untilDestroyed = injectUntilDestroyed();
  private cdRef = inject(ChangeDetectorRef);
  private injector = inject(Injector);

  public ngOnInit() {
    this.watchFields();
  }

  public toggleField(field: Field) {
    if (!field.editable) return;

    this.viewFieldsService.toggle(field.id);
    this.viewsAnalytics.propertyToggle({ propertyId: field.id, selected: !field.selected });
  }

  public onFieldsReorder(event: CdkDragDrop<any>) {
    const field = this.fieldsControl().value[event.previousIndex];
    this.viewFieldsService.reorder(event.previousIndex, event.currentIndex);
    this.viewsAnalytics.propertyReorder({
      propertyId: field.configId,
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
    });
  }

  private watchFields() {
    this.viewFieldsService.availableFields$
      .pipe(
        tap(fields => {
          this.availableFieldsCount = fields.length;
        }),
        switchMap(fields =>
          this.fieldsSearch.valueChanges.pipe(startWith(this.fieldsSearch.value)).pipe(
            map(query => {
              const searchStr = (query ?? '').trim().toLowerCase();
              return fields.filter(field => field.label.toLowerCase().includes(searchStr));
            }),
          ),
        ),
        this.untilDestroyed,
      )
      .subscribe(fields => {
        this.visibleFields = fields;
        this.cdRef.detectChanges();
      });

    toObservable(this.fieldsControl, { injector: this.injector })
      .pipe(
        switchMap(control => control.valueChanges.pipe(startWith(control.value))),
        this.untilDestroyed,
      )
      .subscribe(fields => {
        const selectedFields = fields.filter(field => field.selected);
        this.selectedFieldsCount = selectedFields.length;
        this.cdRef.detectChanges();
      });
  }
}
